import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Textarea,
  Switch,
  Button,
  ButtonGroup,
  Checkbox,
} from "@chakra-ui/react";
import Select from "react-select";

import { Auth } from "../../lib/Auth";
import HighlightedTextBox from "./HighlightedTextBox";
import CustomizeAITextHeaders from "./CustomizeAITextHeaders";
import CustomizeAIPreviewSwitch from "./CustomizeAIPreviewSwitch";
import { bookingFieldDefinitions, bookingFieldsInGroup } from "./bookingFieldDefinitions";


const update = (obj, key, value) => ({ ...obj, [key]: value });

// ── Booking-information collection (toggle UI) ───────────────────────────────
// The toggles below are a view over the three storage arrays
// (booking_blocker_fields / booking_required_fields / booking_optional_fields).
// Field vocabulary and allowed placements: ./bookingFieldDefinitions (served by the api, SS-4258).
// Keys NOT managed by a toggle (legacy CSV entries) are preserved verbatim.
const BOOKING_ARRAYS = [
  "booking_blocker_fields",
  "booking_required_fields",
  "booking_optional_fields",
];

// SS-4258: the field list and each field's allowed placements come from the api
// (see ./bookingFieldDefinitions) — the same list the api validates every save
// against, so a placement offered here is always one the api stores.
const insuranceCoreFields = () => bookingFieldsInGroup("insurance_core");
const insuranceDetailFields = () => bookingFieldsInGroup("insurance_detail");
const afterBookingFields = () => bookingFieldsInGroup("contact");

// Ezra-only, blocker semantics: disables the LLM service-classification
// fallback for ambiguous requests, so the AI asks instead of guessing. An
// exact service-name match in the caller's words still auto-selects (that is
// not a guess), so the label must not overpromise "always asks".
const REASON_FIELD = { field: "reason_for_visit", label: "When the request is ambiguous, ask the reason for visit instead of guessing the service" };

const inAnyBookingArray = (oi, field) =>
  BOOKING_ARRAYS.some(a => (oi?.[a] || []).includes(field));

const fieldIsOn = (oi, f) =>
  [f.field, ...(f.aliases || [])].some(k => inAnyBookingArray(oi, k));


// Dependency rules: a field with dependsOn may only be collected while every
// prerequisite is also collected — otherwise its toggle renders OFF and
// disabled. Every change is normalized so stale dependent keys (from legacy
// CSV configs) are stripped rather than silently kept: Ezra's booking gate
// would otherwise still enforce an orphaned detail key the UI shows as off.
const prereqsMet = (oi, f) =>
  (f.dependsOn || []).every(pf => inAnyBookingArray(oi, pf));

const normalizeDependencies = (oi) => {
  const next = { ...oi };
  let changed = true;
  while (changed) {
    changed = false;
    for (const f of bookingFieldDefinitions()) {
      if (!f.dependsOn || !fieldIsOn(next, f) || prereqsMet(next, f)) continue;
      const keys = [f.field, ...(f.aliases || [])];
      for (const a of BOOKING_ARRAYS) {
        next[a] = (next[a] || []).filter(k => !keys.includes(k));
      }
      changed = true;
    }
  }
  return next;
};

// SS-4292: Medicaid as a third plan type. Off by default; only offered while Plan type is
// asked, and cleared when Plan type is turned off. The api names the choice the same way to
// the AI ("PPO, HMO or Medicaid") and to the configuration check.
const MEDICAID_KEY = "insurance_type_includes_medicaid";
const PLAN_TYPE_FIELD = "insurance_type";
const MEDICAID_LABEL = "Include Medicaid (ask HMO, PPO or Medicaid)";

const toRoutingSelectValue = (arr, options) => {
  if (!Array.isArray(arr)) return [];
  const bids = arr.map(b => (typeof b === "string" ? b : b.bid));
  return options.filter(o => bids.includes(o.value));
};


const CustomizeAIDetail = ({ inEditMode = false, organized_instructions, onChange, PreviewMode = false, associatedBusinesses = [], disableSmsPostAppointment = false, onDisableSmsPostAppointmentChange, }) => {
  const INSURANCE_CORE_FIELDS = insuranceCoreFields();
  const INSURANCE_DETAIL_FIELDS = insuranceDetailFields();
  const AFTER_BOOKING_FIELDS = afterBookingFields();
  const bgColor = useColorModeValue("gray.100", "gray.700");
  // Cards need their own surface — bordered boxes directly on the gray panel
  // background read as visually entangled.
  const cardBg = useColorModeValue("white", "gray.800");
  const setField = (key) => (value) => {
    const next = update(organized_instructions, key, value);
    onChange?.(next);
  };

  // ── Per-field placement (Codex UX round): one fixed row per field with an
  // explicit three-state choice. Exactly maps the storage arrays; no moving
  // blocks, no checkbox-inside-row, mixed legacy configs render exactly.
  const PLACEMENT_ORDER = ["during", "must", "after"];
  const PLACEMENT_TARGET = {
    during: "booking_required_fields",
    must: "booking_blocker_fields",
    after: "booking_optional_fields",
  };
  const PLACEMENT_LABEL = {
    during: "Ask during booking",
    must: "Must answer to book",
    after: "After booking",
  };

  const placementOf = (oi, f) => {
    const keys = [f.field, ...(f.aliases || [])];
    if (keys.some(k => (oi?.booking_blocker_fields || []).includes(k))) return "must";
    if (keys.some(k => (oi?.booking_required_fields || []).includes(k))) return "during";
    if (keys.some(k => (oi?.booking_optional_fields || []).includes(k))) return "after";
    return null;
  };

  // placement null = off. Strips the field and its aliases from all three
  // arrays first (migrating legacy subscriber_id onto policy_number on touch),
  // then normalizes dependencies (unchecking Insurance name turns details off).
  const setFieldState = (fieldDef, placement) => {
    const { field, aliases = [] } = fieldDef;
    const strip = [field, ...aliases];
    const next = {};
    for (const a of BOOKING_ARRAYS) {
      next[a] = (organized_instructions?.[a] || []).filter(k => !strip.includes(k));
    }
    if (placement) {
      next[PLACEMENT_TARGET[placement]] = [...next[PLACEMENT_TARGET[placement]], field];
    }
    const merged = { ...organized_instructions, ...next };
    // Plan type off → its Medicaid option off too: it has nothing to add to.
    if (field === PLAN_TYPE_FIELD && !placement && merged[MEDICAID_KEY]) merged[MEDICAID_KEY] = false;
    onChange?.(normalizeDependencies(merged));
  };

  const planTypeOn = fieldIsOn(organized_instructions, { field: PLAN_TYPE_FIELD });
  const medicaidOn = planTypeOn && !!organized_instructions?.[MEDICAID_KEY];
  const medicaidOption = () => (
    <Flex key={MEDICAID_KEY} align="center" width="100%" mt={1} pl={12}>
      <Checkbox
        size="sm"
        isChecked={medicaidOn}
        isDisabled={!inEditMode || !planTypeOn}
        onChange={(e) => onChange?.(update(organized_instructions, MEDICAID_KEY, e.target.checked))}
        data-testid="plan-type-medicaid"
      >
        <Text fontSize="sm">{MEDICAID_LABEL}</Text>
      </Checkbox>
    </Flex>
  );

  const placementPicker = (f, current) => (
    <ButtonGroup ml="auto" size="xs" isAttached>
      {PLACEMENT_ORDER.filter(pl => (f.states || PLACEMENT_ORDER).includes(pl)).map(pl => (
        <Button
          key={pl}
          variant={current === pl ? "solid" : "outline"}
          onClick={() => setFieldState(f, pl)}
          isDisabled={!inEditMode}
        >
          {PLACEMENT_LABEL[pl]}
        </Button>
      ))}
    </ButtonGroup>
  );

  const fieldRow = (f) => {
    const current = placementOf(organized_instructions, f);
    const on = current !== null && prereqsMet(organized_instructions, f);
    const states = f.states || PLACEMENT_ORDER;
    return (
      <Flex key={f.field} align="center" width="100%" mt={1} pl={f.dependsOn && f.dependsOn.length > 1 ? 6 : 0}>
        <Switch
          isChecked={on}
          onChange={(e) => setFieldState(f, e.target.checked ? states[0] : null)}
          isDisabled={!inEditMode || !prereqsMet(organized_instructions, f)}
        />
        <Text fontSize="sm" ml={2}>{f.label}</Text>
        {on && states.length > 1 && placementPicker(f, current)}
        {on && states.length === 1 && (
          <Text fontSize="xs" color="gray.500" ml="auto">{PLACEMENT_LABEL[states[0]]}</Text>
        )}
      </Flex>
    );
  };

    // Audience scoping (*_only_for_new_patients). Storage is per-tier, exposed
  // per CARD: the During card drives the blocker AND required flags together
  // (one visual unit), the After card drives the optional flag. Mixed legacy
  // flag states display as unset and normalize on first touch.
  const duringAudience = (() => {
    const b = !!organized_instructions?.booking_blocker_fields_only_for_new_patients;
    const r = !!organized_instructions?.booking_required_fields_only_for_new_patients;
    if (b && r) return "new";
    if (!b && !r) return "everyone";
    return "mixed";
  })();
  const afterNewOnly = !!organized_instructions?.booking_optional_fields_only_for_new_patients;

  // Preview: each row shows the audience of ITS tier — during rows follow
  // the blocker+required flags, after rows follow the optional flag. Card-level
  // suffixes would misstate cards that mix placements.
  const ezraRowAudience = (placement) => {
    if (!placement) return "";
    if (placement === "after") return afterNewOnly ? " (new patients only)" : "";
    if (duringAudience === "new") return " (new patients only)";
    if (duringAudience === "mixed") return " (mixed new-patient settings)";
    return "";
  };

  const setDuringAudience = (newOnly) => {
    onChange?.({
      ...organized_instructions,
      booking_blocker_fields_only_for_new_patients: newOnly,
      booking_required_fields_only_for_new_patients: newOnly,
    });
  };
  const setAfterAudience = (newOnly) => {
    onChange?.({ ...organized_instructions, booking_optional_fields_only_for_new_patients: newOnly });
  };
  const audiencePicker = (value, onPick) => (
    <ButtonGroup ml="auto" size="xs" isAttached>
      <Button
        variant={value === "everyone" ? "solid" : "outline"}
        onClick={() => onPick(false)}
        isDisabled={!inEditMode}
      >
        Ask everyone
      </Button>
      <Button
        variant={value === "new" ? "solid" : "outline"}
        onClick={() => onPick(true)}
        isDisabled={!inEditMode}
      >
        New patients only
      </Button>
    </ButtonGroup>
  );

  const routingOptions = associatedBusinesses.map(b => ({ value: b.bid, label: b.name }));

  const [filtersText, setFiltersText] = useState(
    JSON.stringify(organized_instructions?.availabilities_filters ?? [], null, 2)
  );

  useEffect(() => {
    setFiltersText(
      JSON.stringify(organized_instructions?.availabilities_filters ?? [], null, 2)
    );
  }, [organized_instructions?.availabilities_filters]);

  // Availability filters stay a superadmin / partner-admin tool: everything else
  // in this component is for whoever holds BUSINESS_SETTINGS. Only the JSX is
  // gated — availabilities_filters is never stripped from organized_instructions,
  // so an office admin's save round-trips the value untouched.
  const canSeeFilters = !!Auth?.user?.isSuperAdmin;


  return (
    PreviewMode ?
      // in preview mode:
      <Box p={1} bgColor={""} width="full" ml={1}>
        <Text fontSize="m" fontWeight="bold" mb={2}> AI Settings </Text>
        <Box borderRadius="md" width="full" mt={2}>
          <Flex minH="90px" flexDirection="column" gap={2} alignItems={"Start"}>
            {/*<Flex flexDirection="column" width="100%" align="start" px={1}>*/}
            {/*  <CustomizeAITextHeaders input="Interaction Guideline"*/}
            {/*                          description="Include details such as a name for your AI, general tone, etc."/>*/}
            {/*  <HighlightedTextBox input={organized_instructions?.interaction_guideline}/>*/}
            {/*</Flex>*/}
            {/*<Flex flexDirection="column" width="100%" px={1}>*/}
            {/*  <CustomizeAITextHeaders input="Appointment Handling Guideline"*/}
            {/*                          description="Include what information needs to be collected and how to respond to questions about appointment handling."/>*/}
            {/*  <HighlightedTextBox input={organized_instructions?.appointment_handling_guideline}/>*/}
            {/*</Flex>*/}
            {/*<Flex flexDirection="column" width="100%" px={1}>*/}
            {/*  <CustomizeAITextHeaders input="Insurance Guideline"*/}
            {/*                          description="This covers details such as accepted insurance, the verification process, and more."/>*/}
            {/*  <HighlightedTextBox input={organized_instructions?.insurance_guideline}/>*/}
            {/*</Flex>*/}
            {/*<Flex flexDirection="column" width="100%" px={1}>*/}
            {/*  <CustomizeAITextHeaders input="Call Transfer Guideline"*/}
            {/*                          description="Details not listed on the website summary that the AI should know."/>*/}
            {/*  <HighlightedTextBox input={organized_instructions?.call_transfer_guideline}/>*/}
            {/*</Flex>*/}
            {/*<Flex flexDirection="column" width="100%" px={1}>*/}
            {/*  <CustomizeAITextHeaders input="FAQ"*/}
            {/*                          description="Frequently asked question. Questions and answers that are common to your practice."/>*/}
            {/*  <HighlightedTextBox input={organized_instructions?.faq}/>*/}
            {/*</Flex>*/}
            {/*<Flex flexDirection="column" width="100%" px={1}>*/}
            {/*  <CustomizeAITextHeaders input="Practice Optimizer Information"*/}
            {/*                          description="Expxanation of how the office manages its data, such as the process for adding treatments."/>*/}
            {/*  <HighlightedTextBox input={organized_instructions?.practice_optimizer_information}/>*/}
            {/*</Flex>*/}
            {/*<Flex flexDirection="column" width="100%" px={1}>*/}
            {/*  <Text fontSize="sm" fontWeight="bold"> Appointment Information Collection </Text>*/}
            {/*</Flex>*/}
            <Flex flexDirection="column" width="100%" pl={3}>
              <Box borderWidth="1px" borderRadius="md" p={2} mt={1} width="100%" bg={cardBg}>
                <Text fontSize="sm" fontWeight="bold"> Insurance </Text>
                {[...INSURANCE_CORE_FIELDS, ...INSURANCE_DETAIL_FIELDS].map((f) => {
                  const current = placementOf(organized_instructions, f);
                  const on = current !== null && prereqsMet(organized_instructions, f);
                  return (
                    <CustomizeAIPreviewSwitch key={f.field}
                      title={(f.field === PLAN_TYPE_FIELD && medicaidOn ? "Plan type (HMO / PPO / Medicaid)" : f.label)
                        + (on ? ` — ${PLACEMENT_LABEL[current].toLowerCase()}${ezraRowAudience(current)}` : "")}
                      value={on} isDisabled={!inEditMode}/>
                  );
                })}
              </Box>
              <Box borderWidth="1px" borderRadius="md" p={2} mt={1} width="100%" bg={cardBg}>
                <Text fontSize="sm" fontWeight="bold"> Patient information </Text>
                {AFTER_BOOKING_FIELDS.map((f) => {
                  const current = placementOf(organized_instructions, f);
                  const on = current !== null && prereqsMet(organized_instructions, f);
                  return (
                    <CustomizeAIPreviewSwitch key={f.field}
                      title={f.label + (on ? ` — ${PLACEMENT_LABEL[current].toLowerCase()}${ezraRowAudience(current)}` : "")}
                      value={on} isDisabled={!inEditMode}/>
                  );
                })}
                <CustomizeAIPreviewSwitch title={REASON_FIELD.label}
                  value={(organized_instructions?.booking_blocker_fields || []).includes(REASON_FIELD.field)} isDisabled={!inEditMode}/>
              </Box>
              <CustomizeAIPreviewSwitch
                title=" After hours call transfer"
                description="Enable to allow AI to transfer calls after hours to specific extensions."
                value={organized_instructions?.transfer_to_extension_after_hours}
                isDisabled={!inEditMode}
              />
              <CustomizeAIPreviewSwitch
                title="Transfer call for all cancellation"
                description="Enable to always transfer calls for cancellation requests."
                value={organized_instructions?.transfer_call_for_all_cancellation}
                isDisabled={!inEditMode}
              />
              <CustomizeAIPreviewSwitch
                title="Transfer call for all rescheduling"
                description="Enable to always transfer calls for rescheduling requests."
                value={organized_instructions?.transfer_call_for_all_rescheduling}
                isDisabled={!inEditMode}
              />
              <CustomizeAIPreviewSwitch
                title=" Accept medical insurance"
                description="Enable if you accept medical insurance."
                value={organized_instructions?.accept_medical}
                isDisabled={!inEditMode}
              />
              <CustomizeAIPreviewSwitch
                title="Do not notify patients about appointment changes"
                description="When enabled, Viva sends no text or email to the patient after an appointment is created, changed or cancelled. Your office still gets its usual notifications."
                value={disableSmsPostAppointment}
                isDisabled={!inEditMode}
              />
            </Flex>
            {organized_instructions?.businesses_for_routing?.length > 0 && (
              <Flex flexDirection="column" width="100%" pl={3}>
                <HighlightedTextBox header="Businesses for Routing"
                                    description="Locations detected in instructions for call routing/transferring"
                                    input={organized_instructions.businesses_for_routing.map(b => b.name).join(", ")}/>
              </Flex>
            )}
            {canSeeFilters && (
              <Flex flexDirection="column" align="start" width="100%" px={1}>
                <Text fontSize="sm" fontWeight="bold" ml={1}> Filters </Text>
                <HighlightedTextBox
                  input={JSON.stringify(organized_instructions?.availabilities_filters ?? [], null, 2)}/>
              </Flex>
            )}
          </Flex>
        </Box>
      </Box>
      :
      // edit mode
      <Box p={1} bgColor={bgColor} borderRadius="md" width="full">
        <Flex minH="90px" flexDirection="column" gap={2}>
          {/*<Flex flexDirection="column" width="100%" align="start" px={1}>*/}
          {/*  <CustomizeAITextHeaders input="Interaction Guideline"*/}
          {/*                          description="Include details such as a name for your AI, general tone, etc."/>*/}
          {/*  <Textarea*/}
          {/*    value={organized_instructions?.interaction_guideline || ""}*/}
          {/*    isDisabled={!inEditMode}*/}
          {/*    height="100px"*/}
          {/*    onChange={(e) => setField("interaction_guideline")(e.target.value)}*/}
          {/*  />*/}
          {/*</Flex>*/}
          {/*<Flex flexDirection="column" align="start" width="100%" px={1}>*/}
          {/*  <CustomizeAITextHeaders input="Appointment Handling Guideline"*/}
          {/*                          description="Include what information needs to be collected and how to respond to questions about appointment handling."/>*/}
          {/*  <Textarea*/}
          {/*    value={organized_instructions?.appointment_handling_guideline || ""}*/}
          {/*    onChange={(e) => setField("appointment_handling_guideline")(e.target.value)}*/}
          {/*    isDisabled={!inEditMode}*/}
          {/*    height="100px"*/}
          {/*  />*/}
          {/*</Flex>*/}
          {/*<Flex flexDirection="column" align="start" width="100%" px={1}>*/}
          {/*  <CustomizeAITextHeaders input="Insurance Guideline"*/}
          {/*                          description="This covers details such as accepted insurance, the verification process, and more."/>*/}
          {/*  <Textarea*/}
          {/*    value={organized_instructions?.insurance_guideline || ""}*/}
          {/*    onChange={(e) => setField("insurance_guideline")(e.target.value)}*/}
          {/*    isDisabled={!inEditMode}*/}
          {/*    height="100px"*/}
          {/*  />*/}
          {/*</Flex>*/}
          {/*<Flex flexDirection="column" align="start" width="100%" px={1}>*/}
          {/*  <CustomizeAITextHeaders input="Call Transfer Guideline"*/}
          {/*                          description="Details not listed on the website summary that the AI should know."/>*/}
          {/*  <Textarea*/}
          {/*    value={organized_instructions?.call_transfer_guideline || ""}*/}
          {/*    onChange={(e) => setField("call_transfer_guideline")(e.target.value)}*/}
          {/*    isDisabled={!inEditMode}*/}
          {/*    height="100px"*/}
          {/*  />*/}
          {/*</Flex>*/}
          {/*<Flex flexDirection="column" align="start" width="100%" px={1}>*/}
          {/*  <CustomizeAITextHeaders input="FAQ"*/}
          {/*                          description="Frequently asked question. Questions and answers that are common to your practice."/>*/}
          {/*  <Textarea*/}
          {/*    value={organized_instructions?.faq || ""}*/}
          {/*    onChange={(e) => setField("faq")(e.target.value)}*/}
          {/*    isDisabled={!inEditMode}*/}
          {/*    height="100px"*/}
          {/*  />*/}
          {/*</Flex>*/}
          {/*<Flex flexDirection="column" align="start" width="100%" px={1}>*/}
          {/*  <CustomizeAITextHeaders input="Practice Optimizer Information"*/}
          {/*                          description="Expxanation of how the office manages its data, such as the process for adding treatments."/>*/}
          {/*  <Textarea*/}
          {/*    value={organized_instructions?.practice_optimizer_information || ""}*/}
          {/*    onChange={(e) => setField("practice_optimizer_information")(e.target.value)}*/}
          {/*    isDisabled={!inEditMode}*/}
          {/*    height="100px"*/}
          {/*  />*/}
          {/*</Flex>*/}
          <Flex flexDirection="column" align="start" width="100%" px={1}>
            <Text fontSize="sm" fontWeight="bold" mt={2} ml={1}> Appointment Information Collection </Text>
          </Flex>
          <Flex flexDirection="column" align="start" width="100%" pl={3}>
            {/* Per-field placement UI over the three booking arrays (Codex UX
                round): every field is one fixed row with a switch and, when on,
                an explicit placement — "Ask during booking" (required array:
                asked, books anyway), "Must answer to book" (blocker array: the
                booking tool refuses until answered; insurance name/type also
                collected before times are offered), "After booking" (optional
                array: asked once booked, never blocks). The engine enforces
                exactly this in real calls AND demos. Unmanaged legacy keys are
                preserved. */}
            <Box borderWidth="1px" borderRadius="md" p={3} mt={2} width="100%" bg={cardBg}>
              <Text fontSize="sm" fontWeight="bold"> Insurance </Text>
              <Text fontSize="xs" color="gray.500">
                Self-pay always counts as an insurance answer. Fields set to must-answer refuse the booking until collected.
              </Text>
              {INSURANCE_CORE_FIELDS.map((f) => (f.field === PLAN_TYPE_FIELD
                ? [fieldRow(f), medicaidOption()]
                : fieldRow(f)))}
              <Text fontSize="sm" fontWeight="semibold" mt={3}> Insurance details </Text>
              <Text fontSize="xs" color="gray.500"> Require Insurance name. </Text>
              {INSURANCE_DETAIL_FIELDS.map(fieldRow)}
            </Box>
            <Box borderWidth="1px" borderRadius="md" p={3} mt={2} width="100%" bg={cardBg}>
              <Text fontSize="sm" fontWeight="bold"> Patient information </Text>
              <Text fontSize="xs" color="gray.500">
                Answers collected during booking ride the booking: the address is written into the PMS appointment note (and, for new patients, the chart). Email is saved for NEW patients only — an existing patient&apos;s newly given email is not stored. &quot;After booking&quot; questions are asked once the appointment is made, never block it, and their answers stay with the call record — they are not written to the appointment or the chart.
              </Text>
              {AFTER_BOOKING_FIELDS.map(fieldRow)}
            </Box>
            <Box borderWidth="1px" borderRadius="md" p={3} mt={2} width="100%" bg={cardBg}>
              <Text fontSize="sm" fontWeight="bold"> Booking questions </Text>
              <Flex align="center" width="100%" mt={1}>
                <Switch
                  isChecked={(organized_instructions?.booking_blocker_fields || []).includes(REASON_FIELD.field)}
                  onChange={(e) => setFieldState(REASON_FIELD, e.target.checked ? "must" : null)}
                  isDisabled={!inEditMode}
                />
                <Text fontSize="sm" ml={2}>{REASON_FIELD.label}</Text>
              </Flex>
            </Box>
            <Box borderWidth="1px" borderRadius="md" p={3} mt={2} width="100%" bg={cardBg}>
              <Text fontSize="sm" fontWeight="bold"> Who gets asked </Text>
              <Flex align="center" width="100%" mt={1}>
                <Text fontSize="sm">During-booking questions</Text>
                {audiencePicker(duringAudience, setDuringAudience)}
              </Flex>
              <Flex align="center" width="100%" mt={1}>
                <Text fontSize="sm">After-booking questions</Text>
                {audiencePicker(afterNewOnly ? "new" : "everyone", setAfterAudience)}
              </Flex>
            </Box>
            <CustomizeAITextHeaders text={<Text fontSize="sm" mt={2}> After hours call transfer </Text>}
                                    description="Enable to allow AI to transfer calls after hours to specific extensions."/>
            <Switch
              isChecked={!!organized_instructions?.transfer_to_extension_after_hours}
              onChange={(e) => setField("transfer_to_extension_after_hours")(e.target.checked)}
              isDisabled={!inEditMode}
            />
            <CustomizeAITextHeaders text={<Text fontSize="sm" mt={2}> Transfer call for all cancellation </Text>}
                                    description="Enable to always transfer calls for cancellation requests."/>
            <Switch
              isChecked={!!organized_instructions?.transfer_call_for_all_cancellation}
              onChange={(e) => setField("transfer_call_for_all_cancellation")(e.target.checked)}
              isDisabled={!inEditMode}
            />
            <CustomizeAITextHeaders text={<Text fontSize="sm" mt={2}> Transfer call for all rescheduling </Text>}
                                    description="Enable to always transfer calls for rescheduling requests."/>
            <Switch
              isChecked={!!organized_instructions?.transfer_call_for_all_rescheduling}
              onChange={(e) => setField("transfer_call_for_all_rescheduling")(e.target.checked)}
              isDisabled={!inEditMode}
            />
            <CustomizeAITextHeaders text={<Text fontSize="sm" mt={2}> Accept medical insurance </Text>}
                                    description="Enable if you accept medical insurance"/>
            <Switch
              isChecked={!!organized_instructions?.accept_medical}
              onChange={(e) => setField("accept_medical")(e.target.checked)}
              isDisabled={!inEditMode}
            />
            <CustomizeAITextHeaders text={<Text fontSize="sm" mt={2}> Do not notify patients about appointment changes </Text>}
                                    description="When enabled, Viva sends no text or email to the patient after an appointment is created, changed or cancelled. Your office still gets its usual notifications."/>
            <Switch
              isChecked={!!disableSmsPostAppointment}
              onChange={(e) => onDisableSmsPostAppointmentChange?.(e.target.checked)}
              isDisabled={!inEditMode}
            />
          </Flex>
          {associatedBusinesses.length > 0 && (
          <Flex flexDirection="column" align="start" width="100%" pl={3}>
            <CustomizeAITextHeaders text={<Text fontSize="sm" mt={2}> Businesses for Routing </Text>}
                                    description="Select locations that calls can be routed/transferred to"/>
            <Box width="100%">
              <Select
                isMulti
                isDisabled={!inEditMode}
                options={routingOptions}
                value={toRoutingSelectValue(organized_instructions?.businesses_for_routing, routingOptions)}
                onChange={(selected) => {
                  const values = (selected || []).map(opt => ({ bid: opt.value, name: opt.label }));
                  setField("businesses_for_routing")(values);
                }}
                placeholder="Select businesses..."
              />
            </Box>
          </Flex>
          )}
          {canSeeFilters && (
          <Flex flexDirection="column" align="start" width="100%" px={1}>
            <Text fontSize="sm" fontWeight="bold" ml={1}> Filters </Text>
            <Textarea
                value={filtersText}
                onChange={(e) => setFiltersText(e.target.value)}
                onBlur={() => {
                  // only when user leaves the field do we try to parse
                  try {
                    const parsed = JSON.parse(filtersText || "[]");
                    setField("availabilities_filters")(parsed);
                  } catch (err) {
                    console.error("Invalid JSON for availabilities_filters", err);
                  }
                }}
                isDisabled={!inEditMode}
                height="100px"
            />
          </Flex>
          )}
        </Flex>
      </Box>
  )
};

export default CustomizeAIDetail;
